import React, { useState, useEffect } from 'react';
import adminAttendanceService from '../../firebase/adminAttendanceService';

/**
 * AttendancePhotoModal Component
 * Modal for viewing full-size attendance photos with metadata
 */
const AttendancePhotoModal = ({ isOpen, photoUrl, attendanceRecord, onClose }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [currentPhotoUrl, setCurrentPhotoUrl] = useState(photoUrl);
  const [fetchingSignedUrl, setFetchingSignedUrl] = useState(false);

  // Reset states when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setImageLoaded(false);
      setImageError(false);
      setCurrentPhotoUrl(photoUrl);
      setFetchingSignedUrl(false);
      
      // Debug logging
      console.log('🔍 AttendancePhotoModal opened with:', {
        photoUrl: photoUrl,
        attendanceRecordPhotoUrl: attendanceRecord?.photoUrl,
        attendanceId: attendanceRecord?.id
      });
    }
  }, [isOpen, photoUrl, attendanceRecord]);

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'hidden'; // Prevent background scrolling
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const handleImageError = async (event) => {
    const failedUrl = event.target.src;
    console.error('❌ Image failed to load:', failedUrl);
    
    // Log detailed error information
    console.error('🔍 Image load failure details:', {
      failedUrl: failedUrl,
      currentPhotoUrl: currentPhotoUrl,
      originalPhotoUrl: photoUrl,
      attendanceRecordPhotoUrl: attendanceRecord?.photoUrl,
      attendanceId: attendanceRecord?.id,
      timestamp: new Date().toISOString(),
      naturalWidth: event.target.naturalWidth,
      naturalHeight: event.target.naturalHeight,
      complete: event.target.complete,
      fetchingSignedUrl: fetchingSignedUrl
    });
    
    // Check if the failed URL is already a signed URL (contains X-Goog parameters)
    const isSignedUrl = failedUrl.includes('X-Goog-Algorithm=') && failedUrl.includes('X-Goog-Signature=');
    console.log('🔍 Failed URL analysis:', {
      isSignedUrl: isSignedUrl,
      isPublicUrl: failedUrl.includes('/v0/b/') && failedUrl.includes('/o/'),
      isStorageUrl: failedUrl.includes('storage.googleapis.com')
    });
    
    // If we haven't tried fetching a signed URL yet and we have an attendance record
    // AND the failed URL is not already a proper signed URL
    if (!fetchingSignedUrl && attendanceRecord?.id && attendanceRecord?.photoUrl && !isSignedUrl) {
      console.log('🔄 Attempting to fetch signed URL as fallback...');
      setFetchingSignedUrl(true);
      
      try {
        // Try to get a signed URL using the fallback method
        const signedUrl = await adminAttendanceService.getPhotoWithFallback(
          attendanceRecord.id, 
          attendanceRecord.photoUrl
        );
        
        if (signedUrl && signedUrl !== failedUrl) {
          console.log('✅ Got signed URL fallback, updating image source');
          console.log('🔗 New signed URL:', signedUrl.substring(0, 100) + '...');
          console.log('🔍 Signed URL analysis:', {
            isV4SignedUrl: signedUrl.includes('X-Goog-Algorithm=') && signedUrl.includes('X-Goog-Signature='),
            isPublicUrl: signedUrl.includes('/v0/b/') && signedUrl.includes('/o/'),
            startsWithStorage: signedUrl.startsWith('https://storage.googleapis.com/'),
            fullUrl: signedUrl
          });
          setCurrentPhotoUrl(signedUrl);
          setImageError(false);
          setImageLoaded(false);
          // The image will reload automatically due to src change
          return;
        } else {
          console.log('⚠️ Signed URL same as failed URL or empty, showing error');
          console.log('🔍 Comparison details:', {
            signedUrl: signedUrl,
            failedUrl: failedUrl,
            areEqual: signedUrl === failedUrl,
            signedUrlLength: signedUrl?.length,
            failedUrlLength: failedUrl?.length
          });
        }
      } catch (signedUrlError) {
        console.error('❌ Failed to get signed URL fallback:', signedUrlError);
      } finally {
        setFetchingSignedUrl(false);
      }
    } else if (isSignedUrl) {
      console.log('⚠️ Failed URL is already a signed URL, not attempting fallback');
    } else if (fetchingSignedUrl) {
      console.log('⚠️ Already fetching signed URL, not attempting again');
    }
    
    setImageError(true);
    setImageLoaded(true);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString('en-SG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatLocation = (location) => {
    if (!location) return 'N/A';
    return `${location.latitude?.toFixed(6) || 'N/A'}, ${location.longitude?.toFixed(6) || 'N/A'}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return '#52c41a';
      case 'pending': return '#faad14';
      case 'rejected': return '#ff4d4f';
      default: return '#d9d9d9';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved': return '✅';
      case 'pending': return '⏳';
      case 'rejected': return '❌';
      default: return '❓';
    }
  };

  const getValidationScoreColor = (score) => {
    if (score >= 80) return '#52c41a';
    if (score >= 60) return '#faad14';
    return '#ff4d4f';
  };

  if (!isOpen) return null;

  return (
    <div className="photo-modal-overlay" onClick={handleBackdropClick}>
      <div className="photo-modal-content">
        {/* Modal Header */}
        <div className="photo-modal-header">
          <div className="modal-title">
            <h2>📷 Attendance Photo</h2>
            <div className="employee-info">
              <span className="employee-name">{attendanceRecord?.employeeName || 'Unknown Employee'}</span>
              <span className="attendance-type">
                {attendanceRecord?.type === 'clock-in' ? '🟢 Clock In' : '🔴 Clock Out'}
              </span>
            </div>
          </div>
          
          <button className="modal-close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        {/* Photo Section */}
        <div className="photo-modal-body">
          <div className="photo-container">
            {(!imageLoaded && !imageError) || fetchingSignedUrl ? (
              <div className="photo-loading">
                <div className="loading-spinner"></div>
                <p>{fetchingSignedUrl ? 'Fetching secure photo URL...' : 'Loading photo...'}</p>
              </div>
            ) : null}

            {imageError ? (
              <div className="photo-error">
                <div className="error-icon">📷</div>
                <h3>Failed to Load Photo</h3>
                <p>The attendance photo could not be loaded.</p>
                <p className="error-details">
                  This might be due to network issues, the photo being unavailable, or access restrictions.
                </p>
                <div className="error-actions">
                  <button 
                    className="retry-btn"
                    onClick={async () => {
                      setImageError(false);
                      setImageLoaded(false);
                      setFetchingSignedUrl(false);
                      
                      // First try to get a fresh signed URL if we have attendance record
                      if (attendanceRecord?.id && attendanceRecord?.photoUrl) {
                        try {
                          setFetchingSignedUrl(true);
                          const signedUrl = await adminAttendanceService.getPhotoWithFallback(
                            attendanceRecord.id, 
                            attendanceRecord.photoUrl
                          );
                          if (signedUrl) {
                            setCurrentPhotoUrl(signedUrl);
                            return;
                          }
                        } catch (error) {
                          console.error('❌ Failed to get fresh signed URL:', error);
                        } finally {
                          setFetchingSignedUrl(false);
                        }
                      }
                      
                      // Fallback to force reload with timestamp
                      const img = document.querySelector('.attendance-photo');
                      if (img && currentPhotoUrl) {
                        const separator = currentPhotoUrl.includes('?') ? '&' : '?';
                        setCurrentPhotoUrl(`${currentPhotoUrl}${separator}t=${Date.now()}`);
                      }
                    }}
                    disabled={fetchingSignedUrl}
                  >
                    {fetchingSignedUrl ? '⏳ Getting URL...' : '🔄 Retry Loading'}
                  </button>
                  {process.env.NODE_ENV === 'development' && (
                    <button 
                      className="debug-btn"
                      onClick={() => {
                        console.log('🔍 Debug Info:', {
                          photoUrl,
                          attendanceRecord,
                          timestamp: new Date().toISOString()
                        });
                        // Copy debug info to clipboard
                        navigator.clipboard?.writeText(JSON.stringify({
                          photoUrl,
                          attendanceId: attendanceRecord?.id,
                          timestamp: new Date().toISOString()
                        }, null, 2));
                      }}
                    >
                      🐛 Debug Info
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <img
                src={currentPhotoUrl}
                alt="Attendance Photo"
                className={`attendance-photo ${imageLoaded ? 'loaded' : ''}`}
                onLoad={handleImageLoad}
                onError={handleImageError}
              />
            )}
          </div>

          {/* Photo Metadata */}
          {attendanceRecord && (
            <div className="photo-metadata">
              <div className="metadata-section">
                <h3>📋 Record Information</h3>
                <div className="metadata-grid">
                  <div className="metadata-item">
                    <label>Employee:</label>
                    <span>{attendanceRecord.employeeName || 'Unknown Employee'}</span>
                  </div>
                  
                  <div className="metadata-item">
                    <label>Type:</label>
                    <span>
                      {attendanceRecord.type === 'clock-in' ? '🟢 Clock In' : '🔴 Clock Out'}
                    </span>
                  </div>
                  
                  <div className="metadata-item">
                    <label>Status:</label>
                    <span 
                      className="status-badge"
                      style={{ 
                        backgroundColor: getStatusColor(attendanceRecord.status),
                        color: 'white',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontSize: '12px'
                      }}
                    >
                      {getStatusIcon(attendanceRecord.status)} {attendanceRecord.status?.toUpperCase()}
                    </span>
                  </div>
                  
                  <div className="metadata-item">
                    <label>Timestamp:</label>
                    <span>{formatDate(attendanceRecord.timestamp)}</span>
                  </div>
                  
                  <div className="metadata-item">
                    <label>Location:</label>
                    <span>
                      {attendanceRecord.isWarehouse ? '🏢 Warehouse' : '🌍 Job-site'}
                    </span>
                  </div>
                  
                  <div className="metadata-item">
                    <label>GPS Coordinates:</label>
                    <span>{formatLocation(attendanceRecord.location)}</span>
                  </div>
                  
                  <div className="metadata-item">
                    <label>GPS Accuracy:</label>
                    <span>{attendanceRecord.location?.accuracy ? `${attendanceRecord.location.accuracy}m` : 'N/A'}</span>
                  </div>
                </div>
              </div>

              {/* Photo Metadata */}
              {attendanceRecord.photoMetadata && (
                <div className="metadata-section">
                  <h3>📸 Photo Information</h3>
                  <div className="metadata-grid">
                    <div className="metadata-item">
                      <label>Capture Time:</label>
                      <span>{formatDate(attendanceRecord.photoMetadata.captureTime)}</span>
                    </div>
                    
                    <div className="metadata-item">
                      <label>Device Info:</label>
                      <span>{attendanceRecord.photoMetadata.deviceInfo ? 
                        `${attendanceRecord.photoMetadata.deviceInfo.platform || 'Unknown'} - ${attendanceRecord.photoMetadata.deviceInfo.userAgent?.substring(0, 50) || 'Unknown'}...` : 
                        'N/A'}</span>
                    </div>
                    
                    <div className="metadata-item">
                      <label>Live Capture:</label>
                      <span>
                        {attendanceRecord.photoMetadata.isLiveCapture ? '✅ Yes' : '❌ No'}
                      </span>
                    </div>
                    
                    {attendanceRecord.photoMetadata.fileSize && (
                      <div className="metadata-item">
                        <label>File Size:</label>
                        <span>{Math.round(attendanceRecord.photoMetadata.fileSize / 1024)} KB</span>
                      </div>
                    )}
                    
                    {attendanceRecord.photoMetadata.mimeType && (
                      <div className="metadata-item">
                        <label>Format:</label>
                        <span>{attendanceRecord.photoMetadata.mimeType}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Validation Results */}
              {attendanceRecord.validationResults && (
                <div className="metadata-section">
                  <h3>🎯 Validation Results</h3>
                  <div className="validation-summary">
                    <div className="validation-score">
                      <span className="score-label">Overall Score:</span>
                      <span 
                        className="score-value"
                        style={{ 
                          color: getValidationScoreColor(attendanceRecord.validationResults.overallScore || 0),
                          fontWeight: 'bold',
                          fontSize: '18px'
                        }}
                      >
                        {attendanceRecord.validationResults.overallScore || 0}%
                      </span>
                    </div>
                  </div>
                  
                  <div className="validation-checks">
                    <div className={`validation-check ${attendanceRecord.validationResults.photoRecency ? 'pass' : 'fail'}`}>
                      <span className="check-icon">
                        {attendanceRecord.validationResults.photoRecency ? '✅' : '❌'}
                      </span>
                      <span className="check-label">Photo Recency</span>
                      <span className="check-description">
                        Photo was taken recently and matches submission time
                      </span>
                    </div>
                    
                    <div className={`validation-check ${attendanceRecord.validationResults.gpsAccuracy ? 'pass' : 'fail'}`}>
                      <span className="check-icon">
                        {attendanceRecord.validationResults.gpsAccuracy ? '✅' : '❌'}
                      </span>
                      <span className="check-label">GPS Accuracy</span>
                      <span className="check-description">
                        GPS coordinates are accurate and reliable
                      </span>
                    </div>
                    
                    <div className={`validation-check ${attendanceRecord.validationResults.warehouseProximity ? 'pass' : 'fail'}`}>
                      <span className="check-icon">
                        {attendanceRecord.validationResults.warehouseProximity ? '✅' : '❌'}
                      </span>
                      <span className="check-label">Location Verification</span>
                      <span className="check-description">
                        Location matches expected attendance area
                      </span>
                    </div>
                    
                    <div className={`validation-check ${attendanceRecord.validationResults.duplicateCheck ? 'pass' : 'fail'}`}>
                      <span className="check-icon">
                        {attendanceRecord.validationResults.duplicateCheck ? '✅' : '❌'}
                      </span>
                      <span className="check-label">Duplicate Check</span>
                      <span className="check-description">
                        No duplicate submissions detected
                      </span>
                    </div>
                    
                    <div className={`validation-check ${attendanceRecord.validationResults.photoAuthenticity ? 'pass' : 'fail'}`}>
                      <span className="check-icon">
                        {attendanceRecord.validationResults.photoAuthenticity ? '✅' : '❌'}
                      </span>
                      <span className="check-label">Photo Authenticity</span>
                      <span className="check-description">
                        Photo appears to be authentic and unmodified
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Notes */}
              {(attendanceRecord.notes || attendanceRecord.adminNotes) && (
                <div className="metadata-section">
                  <h3>📝 Notes</h3>
                  {attendanceRecord.notes && (
                    <div className="note-item">
                      <label>Employee Notes:</label>
                      <p>{attendanceRecord.notes}</p>
                    </div>
                  )}
                  {attendanceRecord.adminNotes && (
                    <div className="note-item">
                      <label>Admin Notes:</label>
                      <p>{attendanceRecord.adminNotes}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="photo-modal-footer">
          <div className="modal-actions">
            <button className="modal-btn secondary" onClick={onClose}>
              Close
            </button>
            {currentPhotoUrl && !imageError && (
              <a 
                href={currentPhotoUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="modal-btn primary"
              >
                🔗 Open in New Tab
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttendancePhotoModal;
